import { Box_t, SlipSheet_t } from 'blackbox';
import {
	Route,
	useLocation,
	Switch,
	Redirect,
	useHistory,
	Prompt,
} from 'react-router-dom';

import {
	KeyboardContext,
	KeyboardTrigger,
	LayoutContent,
	Loading,
} from './Layout';
import { TabOption } from './Sidebar';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { fetchCoreAppConfig, Scroll, updateCoreAppConfig, useCoreAppConfig, useMachineConfig } from './General';
import { Application, UnitsSystem, UserProfiles } from 'libapi';
import {
	COLOR,
	UNITS,
	from_unit,
	getChangeWrapperNumber,
	getChangeWrapperString,
	renderInputLength,
	render_length,
	render_length_unit,
	render_weight_unit,
	isWebDeployment,
} from './shared/shared';
import Button from './PendantButton';
import {
	ApplicationContext,
	ConfigurationsContext,
	PreferencesContext,
} from '../App';
import { AppSettings } from './Preferences';
import {
	Calibration_t,
	SlipSheetPickCalibration_t,
	to_calibration,
} from './calibration/Calibration';
import Modal, { Unlock } from './Modal';
import Grippers from './Grippers';

interface SideMenuProps {
	sections: string[];
	sectionSubstituteValues?: string[]; // optional subtitue to return this value instead of section name. must be same len as sections
	selectedSection: string;
	onSelectSection: (section: string) => void;
	unshaded?: boolean;
	subtitles?: string[];
}

export const SideMenu: React.FC<SideMenuProps> = ({
	sections,
	sectionSubstituteValues,
	selectedSection,
	onSelectSection,
	unshaded,
	subtitles,
}) => {
	const lButtonClass =
		'full-width-minus-pad vh-9 flex-col flex-center align-start rounded margin-bottom margin-top color-blue' +
		(!!unshaded ? ' ' : ' background-silver');
	const lSelectedClass = !!unshaded
		? ' bold-font background-silver'
		: ' bold-font blue-inner-border ';

	const lUnselectedClass = !!unshaded ? ' border' : ' ';
	return (
		<Scroll className="flex-col align-center" showScrollBar={true}>
			{sections.map((section, pIdx) => {
				const s = !!sectionSubstituteValues
					? sectionSubstituteValues[pIdx]
					: section;
				return (
					<div
						key={s}
						style={{
							maxWidth: '276px',
							overflow: 'hidden',
							boxSizing: 'border-box',
							paddingTop: '0.5em',
							paddingBottom: '0.5em',
							minHeight: 'fit-content',
						}}
						className={
							lButtonClass +
							` ${s === selectedSection ? lSelectedClass : lUnselectedClass}`
						}
						onClick={() => {
							onSelectSection(s);
						}}
					>
						<div
							className="flex-col align-center double-margin-left"
							style={{
								maxWidth: 'calc(100% - 3em)',
								overflow: 'hidden',
								boxSizing: 'border-box',
							}}
						>
							<span
								className="font-5"
								style={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									maxWidth: '100%',
								}}
							>
								{section}
							</span>
						</div>
						{subtitles && subtitles[pIdx] && (
							<div className="flex-col align-center double-margin-left half-margin-top">
								<span className="color-blue">{subtitles[pIdx]}</span>
							</div>
						)}
					</div>
				);
			})}
			<div className="double-padding" />
			<div className="double-padding" />
			<div className="double-padding" />
			<div className="double-padding" />
			<div className="double-padding" />
		</Scroll>
	);
};

interface SettingsEditorProps {
	settings: Record<string, any>;
	onChange: (key: string, value: any, param?: string) => void;
	canBeHidden?: boolean;
	activeUser?: UserProfiles;
}

export interface WrappedInputProps {
	inputType?: string;
	field: string;
	value: number | string;
	unitsSystem?: UnitsSystem;
	setter: (val: number | string) => void;
	triggerKeyboard: KeyboardTrigger;
	placeholder?: string;
	numericalRange?: number[];
}

export function WrappedInput({
	inputType,
	field,
	value,
	unitsSystem,
	setter,
	triggerKeyboard,
	placeholder,
	numericalRange,
}: WrappedInputProps) {
	const [invalidValueError, setInvalidValueError] = useState<string>('');
	function checkRange(pValue: number) {
		if (!!numericalRange && numericalRange.length == 2) {
			if (pValue < numericalRange[0] || pValue > numericalRange[1]) return false;
		}
		return true;
	}
	const lInputHandlers = getChangeWrapperNumber(function (pNewValue: number) {
		if (UnitsSystem.IMPERIAL === unitsSystem) {
			pNewValue = from_unit(pNewValue, UNITS.inches);
		}
		if (checkRange(pNewValue)) {
			setter(pNewValue);
		} else if (!!numericalRange && numericalRange.length == 2) {
			setInvalidValueError(
				`Value must be between ${numericalRange[0]} and ${numericalRange[1]}`
			);
		}
	});
	const lStringHandler = getChangeWrapperString(function (pNewValue: string) {
		setter(pNewValue);
	});
	const lTriggerKeyboard = function (pField: string, inputType?: string) {
		if (triggerKeyboard) {
			if (typeof value == 'number') {
				if (!!unitsSystem) {
					triggerKeyboard(
						`Enter ${toHumanString(pField)}`,
						renderInputLength(value, unitsSystem),
						lInputHandlers.FromString
					);
				} else {
					triggerKeyboard(
						`Enter ${toHumanString(pField)}`,
						String(value),
						lInputHandlers.FromString
					);
				}
			} else if (typeof value == 'string') {
				triggerKeyboard(
					`Enter ${toHumanString(pField)}`,
					value,
					lStringHandler.Direct,
					inputType
				);
			}
		}
	};

	return (
		<div className="">
			{invalidValueError.length > 0 && (
				<span className="color-red font-3">{invalidValueError}</span>
			)}
			<input
				className="background-transparent no-border text-align-center full-width-minus-pad"
				type="text"
				value={
					!!unitsSystem && typeof value === 'number'
						? renderInputLength(value, unitsSystem)
						: value
				}
				onChange={
					typeof value === 'number' ? lInputHandlers.Event : lStringHandler.Event
				}
				onClick={() => lTriggerKeyboard(field, inputType)}
				placeholder={placeholder ? placeholder : void 0}
			/>
		</div>
	);
}

interface AnimatedToggleProps {
	pChecked: boolean;
	pCheckChanged: (pChecked: boolean) => void;
}

export function AnimatedToggle({
	pChecked,
	pCheckChanged,
}: AnimatedToggleProps) {
	const [checked, setChecked] = useState(pChecked);

	return (
		<div
			className={`toggle ${checked ? 'toggle-checked' : ''}`}
			onClick={() => {
				pCheckChanged(!checked);
				setChecked(!checked);
			}}
		>
			<div className="toggle-circle"></div>
		</div>
	);
}

interface ComboProps {
	values: string[] | number[];
	currentValue: string | number;
	onChange: (value: string | number) => void;
}

export const ComboBox: React.FC<ComboProps> = ({
	values,
	currentValue,
	onChange,
}) => {
	return (
		<select
			className="StyledSelect"
			value={currentValue}
			onChange={e => {
				let v = e.target.value;
				if (v.match(/^\d+$/)) {
					onChange(Number(v));
					return;
				}
				onChange(v);
			}}
		>
			{values.map(value => (
				<option key={value} value={value}>
					{value}
				</option>
			))}
		</select>
	);
};

interface Data {
	[key: string]: any;
}

interface ArrayRenderProps {
	value: (number | string)[];
	arrayLabels: string[];
	unitsSystem: UnitsSystem;
	onChange: (pNewValue: (number | string)[]) => void;
	numericalRanges?: number[][];
}

const ArrayRender: React.FC<ArrayRenderProps> = ({
	value,
	arrayLabels,
	unitsSystem,
	onChange,
	numericalRanges,
}) => {
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);
	return (
		<div className="grid-col-33-33-33">
			{value &&
				arrayLabels &&
				value.length === arrayLabels.length &&
				value.map((v, index) => (
					<div
						key={index}
						className="padding-left padding-right margin-top flex-col"
					>
						<span className="font-5 color-blue">{arrayLabels[index]}</span>
						<WrappedInput
							field={arrayLabels[index]}
							value={v}
							unitsSystem={unitsSystem}
							setter={pNewValue => {
								const cp = [...value];
								cp[index] = pNewValue;
								onChange(cp);
							}}
							triggerKeyboard={lKeyboardTrigger}
							numericalRange={!!numericalRanges ? numericalRanges[index] : void 0}
						/>
					</div>
				))}
		</div>
	);
};

interface IORenderProps {
	net: number;
	pin: number;
	inverse_logic?: boolean;
	enabled?: boolean;
	digital?: boolean;
	external?: boolean;
	onChange: (pNewValue: { [key: string]: any }) => void;
}
export const IORender: React.FC<IORenderProps> = ({
	net,
	pin,
	inverse_logic,
	enabled,
	onChange,
	digital,
	external,
}) => {
	const lKeyboardTrigger = useContext(KeyboardContext);

	// Determine if elements should be disabled
	const isDisabled = enabled === false;

	const handleChange = (key: string, value: any) => {
		const changeObj: { [key: string]: any } = {
			net,
			pin,
		};

		if (inverse_logic !== undefined) {
			changeObj['ReverseLogic'] = inverse_logic;
		}

		// Only add the enabled key if it's explicitly passed
		if (enabled !== undefined) {
			changeObj.enabled = enabled;
		}

		if (digital !== undefined) {
			changeObj.digital = digital;
		}

		if (external !== undefined) {
			changeObj.external = external;
		}

		changeObj[key] = value;

		onChange(changeObj);
	};

	return (
		<div className={`flex-row flex-between`}>
			<div className="flex-row">
				<div
					className={`flex-row align-center ${isDisabled ? 'disabled-style' : ''}`}
				>
					<span className="font-5 double-padding-right">Net</span>

					<ComboBox
						values={[1, 2, 3, 4, 5, 6, 7, 8]}
						currentValue={net}
						onChange={pNewValue => {
							handleChange('net', Number(pNewValue));
						}}
					/>
				</div>
				<div
					className={` double-margin-left flex-row align-center ${
						isDisabled ? 'disabled-style' : ''
					}`}
				>
					<span className="font-5 double-padding-right">Pin</span>
					<ComboBox
						values={[0, 1, 2, 3]}
						currentValue={pin}
						onChange={pNewValue => {
							handleChange('pin', Number(pNewValue));
						}}
					/>
				</div>
				{inverse_logic !== undefined && (
					<div
						className={`double-margin-left flex-row align-center ${
							isDisabled ? 'disabled-style' : ''
						}`}
					>
						<span className="font-5 double-padding-right">Inverse Logic</span>
						<AnimatedToggle
							pChecked={inverse_logic}
							pCheckChanged={pNewValue => handleChange('ReverseLogic', pNewValue)}
						/>
					</div>
				)}
			</div>
			{enabled !== undefined && (
				<div className={`double-margin-left flex-row align-center`}>
					<span className="font-5 double-padding-right">Enabled</span>
					<AnimatedToggle
						pChecked={enabled}
						pCheckChanged={pNewValue => handleChange('enabled', pNewValue)}
					/>
				</div>
			)}
			{digital !== undefined && (
				<div
					className={`double-margin-left flex-row align-center ${
						isDisabled ? 'disabled-style' : ''
					}`}
				>
					<span className="font-5 double-padding-right">Digital</span>
					<AnimatedToggle
						pChecked={digital}
						pCheckChanged={pNewValue => handleChange('digital', pNewValue)}
					/>
				</div>
			)}
			{external !== undefined && (
				<div
					className={`double-margin-left flex-row align-center ${
						isDisabled ? 'disabled-style' : ''
					}`}
				>
					<span className="font-5 double-padding-right">External</span>
					<AnimatedToggle
						pChecked={external}
						pCheckChanged={pNewValue => handleChange('external', pNewValue)}
					/>
				</div>
			)}
		</div>
	);
};

interface RecursiveRendererProps {
	data: Data;
	level?: number;
}

const RecursiveRenderer: React.FC<RecursiveRendererProps> = ({
	data,
	level = 0,
}) => {
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);

	const isObject = (value: any): boolean =>
		typeof value === 'object' && value !== null && !Array.isArray(data);

	return (
		<div>
			{Object.entries(data).map(([key, value]) => (
				<div key={key} className={''} style={{ marginLeft: `${level * 5}%` }}>
					{isObject(value) ? (
						<>
							<span className="font-5 color-blue bold-font double-margin">{key}</span>
							<RecursiveRenderer data={value} level={level + 1} />
						</>
					) : (
						<div className="margin-bottom flex-col">
							<span className="font-5 color-blue double-padding-right margin-bottom">
								{key}
							</span>
							<WrappedInput
								field={key}
								value={value}
								unitsSystem={void 0}
								setter={() => {}}
								triggerKeyboard={lKeyboardTrigger}
							/>
						</div>
					)}
				</div>
			))}
		</div>
	);
};

enum UnitType {
	Distance = 'distance',
	Weight = 'weight',
}

const SettingsEditor: React.FC<SettingsEditorProps> = ({
	settings,
	onChange,
	canBeHidden,
}) => {
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);

	// useEffect(() => {
	// }, [settings]);
	const { preferences } = useContext(PreferencesContext) as any;
	const isUserVention = preferences.active_user_name === 'Vention';

	function getUnits(unit_type: string): any[] {
		if (unit_type === undefined || unit_type === null) return ['', void 0];
		if (unit_type === UnitType.Distance)
			return [
				` (${render_length_unit(preferences.units_system)})`,
				preferences.units_system,
			];
		if (unit_type === UnitType.Weight)
			return [
				` (${render_weight_unit(preferences.units_system)})`,
				preferences.units_system,
			];
		return ['', void 0];
	}
	return (
		<Scroll className="flex-col align-center full-width full-height">
			<div className="padding"></div>
			{Object.entries(settings).map(([key, value]) => {
				const [unit_suffix, unit_system] = getUnits(value.unit_type);
				return (
					<>
						{(!value.hidden === true || isUserVention) && (
							<div key={key} className="flex-col width-90">
								<div style={{ paddingBottom: '0.5vw', display: 'inline-flex' }}>
									<span
										className="font-5 margin-bottom bold-font color-blue"
										style={{ lineHeight: '1.3' }}
									>
										{value.l + unit_suffix}
									</span>
									{canBeHidden && isUserVention && (
										<img
											src={`/static/images/${value.hidden === true ? 'hide' : 'show'}.png`}
											style={{
												width: '1.8vw',
												height: '1.8vw',
												paddingLeft: '1vw',
												cursor: 'pointer',
											}}
											alt="hide property"
											onClick={() => {
												onChange(key, !value.hidden, 'hidden');
											}}
										/>
									)}
								</div>
								{(() => {
									if (
										typeof value.v === 'object' &&
										value.t != 'array' &&
										value.t != 'io'
									) {
										return <RecursiveRenderer data={value.v} />;
									} else {
										switch (value.t) {
											case 'text':
												return (
													<WrappedInput
														field={value.l}
														value={value.v}
														unitsSystem={void 0}
														setter={pNewValue => {
															onChange(key, pNewValue);
														}}
														triggerKeyboard={lKeyboardTrigger}
													/>
												);
											case 'toggle':
												return (
													<AnimatedToggle
														pChecked={value.v}
														pCheckChanged={pChecked => {
															onChange(key, pChecked);
														}}
													/>
												);
											case 'number':
												return (
													<WrappedInput
														field={value.l}
														value={value.v}
														unitsSystem={unit_system as UnitsSystem}
														setter={pNewValue => {
															onChange(key, pNewValue);
														}}
														triggerKeyboard={lKeyboardTrigger}
														numericalRange={value.range}
													/>
												);
											case 'dropdown':
												return (
													<ComboBox
														values={value.choices}
														currentValue={value.v}
														onChange={pNewValue => {
															onChange(key, pNewValue);
														}}
													/>
												);
											case 'array':
												return (
													<ArrayRender
														value={value.v}
														arrayLabels={value.array_labels}
														unitsSystem={unit_system as UnitsSystem}
														onChange={pNewValue => {
															onChange(key, pNewValue);
														}}
														numericalRanges={value.ranges}
													/>
												);
											case 'io':
												return (
													<IORender
														net={value.v.net}
														pin={value.v.pin}
														inverse_logic={value.v.ReverseLogic}
														enabled={value.v.enabled}
														digital={value.v.digital}
														external={value.v.external}
														onChange={pNewValue => {
															onChange(key, pNewValue);
														}}
													/>
												);
											default:
												return null;
										}
									}
								})()}
								<span className="font-3 margin-top color-dark-grey">{value.help}</span>
								<div className="double-padding"></div>
							</div>
						)}
					</>
				);
			})}
			<div className="double-padding"></div>
			<div className="double-padding"></div>
			<div className="double-padding"></div>
			<div className="double-padding"></div>
			<div className="double-padding"></div>
			<div className="double-padding"></div>
		</Scroll>
	);
};

function IOSettings({
	handleMadeChanges,
	confirmSave,
	activeUser,
}: SettingBaseProps) {
	const app = useContext(ApplicationContext) as Application;

	const [config, setConfig] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [original, setOriginal] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [madeChanges, setMadeChanges] = useState<boolean>(false);

	const [selectedSection, setSelectedSection] = useState<string>('');
	useEffect(() => {
		handleMadeChanges(madeChanges);
	}, [madeChanges]);

	const IS_WEB_DEPLOYMENT = isWebDeployment();

	async function fetchConfig() {
		try {
			let pConfig: any;
			if (IS_WEB_DEPLOYMENT) {
				const unparsedSkuConfig = localStorage.getItem('IOConfig');
				pConfig = unparsedSkuConfig
					? JSON.parse(unparsedSkuConfig)
					: await app.api.CoreIOConfig.get();
			} else {
				pConfig = await app.api.CoreIOConfig.get();
			}
			if (selectedSection === '') {
				setSelectedSection(Object.keys(pConfig)[0]);
			}

			setConfig(pConfig);
			setOriginal(JSON.parse(JSON.stringify(pConfig)));
			setMadeChanges(false);
		} catch (error) {
			console.error(error);
		}
	}
	useEffect(function () {
		fetchConfig();
	}, []);

	const handleSelectSection = (section: string) => {
		setSelectedSection(section);
	};

	const handleChange = async (key: string, value: any, param?: string) => {
		const updated_config = JSON.parse(JSON.stringify(config));
		updateValue(updated_config, key, value, param);
		setConfig(updated_config);
		setMadeChanges(JSON.stringify(original) !== JSON.stringify(updated_config));
	};

	const saveChanges = async () => {
		const callback = () => {
			try {
				if (IS_WEB_DEPLOYMENT) {
					localStorage.setItem('IOConfig', JSON.stringify(config));
				} else {
					app.api.CoreIOConfig.update(config);
				}
				console.log('Successfully saved core io config');
				fetchConfig();
			} catch (error) {
				console.error(error);
			}
		};
		confirmSave(callback);
	};

	return (
		<>
			{!!config && config[selectedSection] && (
				<div className="full-size flex-col">
					<div className="flex-row full-width flex-between align-center vh-10 padding-bottom border-bottom">
						<span className="double-margin-left double-margin-top font-5  bold-font">
							Digital IO
						</span>
						<div className="flex-row full-height align-center">
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={fetchConfig}
								pNormalColorOverride={COLOR.DARK_BLUE}
							>
								<span className="bold-font">Cancel</span>
							</Button>
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={saveChanges}
								pNormalColorOverride={madeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
								pDisabled={!madeChanges}
							>
								<span className="bold-font">Save</span>
							</Button>
						</div>
					</div>
					<div className="full-width grid-col-20 margin-top">
						<div className="full-size flex-col flex-start vh-91 margin-left">
							<SideMenu
								sections={Object.keys(config)}
								selectedSection={selectedSection}
								onSelectSection={handleSelectSection}
								unshaded={true}
							/>
						</div>
						<div className="full-size flex-col">
							<SettingsEditor
								settings={config[selectedSection]}
								onChange={handleChange}
								canBeHidden={true}
								activeUser={activeUser}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

function VRDFSettings({ handleMadeChanges, confirmSave }: SettingBaseProps) {
	// // refactor with app config settings later
	// const config = useVRDFConfig();

	const app = useContext(ApplicationContext) as Application;

	const [config, setConfig] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [original, setOriginal] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [madeChanges, setMadeChanges] = useState<boolean>(false);

	useEffect(() => {
		handleMadeChanges(madeChanges);
	}, [madeChanges]);

	function fetchConfig() {
		app.api.VRDFConfig.get()
			.then(function (pConfig) {
				setConfig(pConfig);
				setOriginal(JSON.parse(JSON.stringify(pConfig)));
				setMadeChanges(false);
			})
			.catch(console.error);
	}
	useEffect(function () {
		fetchConfig();
	}, []);

	const handleChange = async (key: string, value: any) => {
		const updated_config = JSON.parse(JSON.stringify(config));
		updateValue(updated_config, key, value);
		setConfig(updated_config);
		setMadeChanges(JSON.stringify(original) !== JSON.stringify(updated_config));
	};

	const saveChanges = async () => {
		confirmSave(() => {
			app.api.VRDFConfig.update(config)
				.then(() => {
					console.log('Saved VRDF config successfully');
					fetchConfig();
				})
				.catch(console.error);
		});
	};

	return (
		<>
			{!!config && (
				<div className="full-size flex-col">
					<div className="flex-row full-width flex-between align-center vh-10 padding-top padding-bottom border-bottom">
						<span className="double-margin-left double-margin-top font-5 bold-font">
							Robot Specific
						</span>
						<div className="flex-row full-height align-center">
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={fetchConfig}
								pNormalColorOverride={COLOR.DARK_BLUE}
							>
								<span className="bold-font">Cancel</span>
							</Button>
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={saveChanges}
								pNormalColorOverride={madeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
								pDisabled={!madeChanges}
							>
								<span className="bold-font">Save</span>
							</Button>
						</div>
					</div>
					<div className="full-size flex-col">
						<SettingsEditor settings={config} onChange={handleChange} />
					</div>
				</div>
			)}
		</>
	);
}

function updateValue(
	obj: { [key: string]: any },
	key: string,
	newValue: any,
	param?: string
) {
	// Iterate through each property in the object
	for (const prop in obj) {
		// Check if the current property is the key we're looking for
		if (prop === key) {
			obj[prop][param || 'v'] = newValue; // Update the value
		} else if (typeof obj[prop] === 'object' && obj[prop] !== null) {
			// If the property is an object, recursively search for the key within it
			updateValue(obj[prop], key, newValue, param);
		}
	}
}

export interface SettingBaseProps {
	handleMadeChanges: (madeChanges: boolean) => void;
	confirmSave: (callback: CallbackType) => void;
	activeUser?: UserProfiles;
}

interface AppConfigSettingsProps extends SettingBaseProps {
	slipSheetPresent: boolean;
}

function AppConfigSettings({
	slipSheetPresent,
	handleMadeChanges,
	confirmSave,
	activeUser,
}: AppConfigSettingsProps) {
	const app = useContext(ApplicationContext) as Application;

	const [config, setConfig] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [original, setOriginal] = useState<undefined | { [key: string]: any }>(
		void 0
	);
	const [madeChanges, setMadeChanges] = useState<boolean>(false);

	const [selectedSection, setSelectedSection] = useState<string>('');

	const { refreshCoreAppConfig } = useCoreAppConfig(app);

	useEffect(() => {
		handleMadeChanges(madeChanges);
	}, [madeChanges]);

	async function fetchConfig() {
		console.log('fetchinf config');
		const pConfig = await fetchCoreAppConfig(app);
		console.log('APP CONFIG', fetchConfig);
		if (!pConfig) return;
		if (selectedSection === '') {
			setSelectedSection(Object.keys(pConfig)[0]);
		}

		setConfig(pConfig);
		setOriginal(JSON.parse(JSON.stringify(pConfig)));
		setMadeChanges(false);
	}
	useEffect(function () {
		console.log('USE EFFECT CALLED');
		fetchConfig();
	}, []);

	const handleSelectSection = (section: string) => {
		setSelectedSection(section);
	};

	const handleChange = async (key: string, value: any, param?: string) => {
		const updated_config = JSON.parse(JSON.stringify(config));
		updateValue(updated_config, key, value, param);
		setConfig(updated_config);
		setMadeChanges(JSON.stringify(original) !== JSON.stringify(updated_config));
	};

	const saveChanges = async () => {
		confirmSave(async () => {
			if (!!config) {
				await updateCoreAppConfig(config, refreshCoreAppConfig, app);
			}
			fetchConfig();
		});
	};

	return (
		<>
			{!!config && (
				<div className="full-size flex-col">
					<div className="flex-row full-width flex-between align-center vh-10 padding-top padding-bottom border-bottom">
						<span className="double-margin-left double-margin-top font-5 bold-font">
							App Config
						</span>
						<div className="flex-row full-height align-center">
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={fetchConfig}
								pNormalColorOverride={COLOR.DARK_BLUE}
							>
								<span className="bold-font">Cancel</span>
							</Button>
							<Button
								className="vw-8 double-margin-right double-margin-top"
								pAction={saveChanges}
								pNormalColorOverride={madeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
								pDisabled={!madeChanges}
							>
								<span className="bold-font">Save</span>
							</Button>
						</div>
					</div>
					<div className="full-height full-width grid-col-20 ">
						<div className="full-size flex-col flex-start vh-91 margin-left padding-top">
							<SideMenu
								sections={Object.keys(config).filter(item =>
									slipSheetPresent ? true : !item.toLowerCase().includes('slip sheet')
								)}
								selectedSection={selectedSection}
								onSelectSection={handleSelectSection}
								unshaded={true}
							/>
						</div>
						<div className="full-size flex-col border-left double-margin-left vh-80">
							<SettingsEditor
								settings={config[selectedSection]}
								onChange={handleChange}
								canBeHidden={true}
								activeUser={activeUser}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
}

enum ConfigType {
	General = 'General',
	AppConfig = 'AppConfig',
	// VRDFConfig = 'VRDFConfig',
	IO = 'IO',
	GRIPPER = 'GRIPPER',
	SLIPSHEET_MAP = 'SLIPSHEET_MAP',
	SKU_SPECIFIC = 'SKU_SPECIFIC',
}

const ConfigTypeDisplayNames: { [key in ConfigType]: string } = {
	[ConfigType.General]: 'General',
	[ConfigType.AppConfig]: 'App Config',
	// [ConfigType.VRDFConfig]: 'Robot Specific',
	[ConfigType.IO]: 'Digital IO',
	[ConfigType.GRIPPER]: 'Gripper',
	[ConfigType.SLIPSHEET_MAP]: 'Slip Sheet Map',
	[ConfigType.SKU_SPECIFIC]: 'SKU Settings',
};

type ConfigTypeValue = (typeof ConfigTypeDisplayNames)[ConfigType];

interface ConfirmationModalProps {
	t1: string;
	t2: string;
	bCancel: string;
	bConfirm: string;
	onConfirm: () => void;
	onCancel: () => void;
	onOutsideClick?: () => void;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	t1,
	t2,
	bCancel,
	bConfirm,
	onConfirm,
	onCancel,
	onOutsideClick,
}) => {
	const modalRef = useRef<HTMLDivElement>(null);

	return (
		<Modal close={onOutsideClick ? onOutsideClick : () => {}}>
			<div ref={modalRef} className="Confirm double-padding flex-col flex-between">
				<span className="font-5 color-blue text-align-center">
					{t1} <br /> {t2}
				</span>
				<div className="flex-row flex-between">
					<Button className="double-padding vw-12" pAction={onCancel}>
						<span>{bCancel}</span>
					</Button>
					<Button
						className="double-padding vw-12"
						pAction={onConfirm}
						pNormalColorOverride={COLOR.LIGHT_BLUE}
					>
						<span>{bConfirm}</span>
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export interface SettingsProps {
	pTab: TabOption;
}

type CallbackType = () => void;

export default function Settings({ pTab }: SettingsProps) {
	const app = useContext(ApplicationContext) as Application;

	const [locked, setLocked] = useState<boolean>(true);

	const history = useHistory();
	const location = useLocation();
	const { lMachineConfig } = useMachineConfig(app);
	const [slipSheetPresent, setSlipSheetPresent] = useState<boolean>(true);
	const [madeChanges, setMadeChanges] = useState<boolean>(false);
	const [showConfirmLeaveModal, setShowConfirmLeaveModal] =
		useState<boolean>(false);
	const [nextLocation, setNextLocation] = useState<any>(null);

	const [confirmSaveCallback, setConfirmSaveCallback] = useState<
		CallbackType | undefined
	>(undefined);
	const [showConfirmSave, setShowConfirmSave] = useState<boolean>(false);
	const [selectedUserObject, setSelectedUserObject] =
		useState<UserProfiles | null>(null);

	const lBase = '/Settings/';

	//updates the selected user object for hidden fields in settings
	useEffect(() => {
		(async () => {
			try {
				const genericConfig = await app.api.Generic.get();
				const lSelectedUserObject = genericConfig.preferences.users.find(
					(user: UserProfiles) =>
						user.name === genericConfig.preferences.active_user_name
				);
				setSelectedUserObject(lSelectedUserObject);
			} catch (error) {
				console.error('Error fetching generic config', error);
			}
		})();
	}, [app]);

	useEffect(() => {
		let lHas = false;
		lMachineConfig?.Bays.forEach(bay => {
			if (bay.Enabled) {
				bay.SlipSheetMags.forEach(m => {
					lHas = m || lHas;
				});
			}
		});
		setSlipSheetPresent(lHas);
	}, [lMachineConfig]);

	// Extract the last segment from the URL and ensure it is a valid ConfigType, or default to ConfigType.General
	const pathSection = location.pathname.split('/').pop() || ConfigType.General; // This line provides a default directly.
	const selectedSection = Object.values(ConfigType).includes(
		pathSection as ConfigType
	)
		? (pathSection as ConfigType)
		: ConfigType.General;

	const handleBlockedNavigation = (nextLoc: any) => {
		// Improve typing here
		if (!madeChanges) return true;
		setShowConfirmLeaveModal(true);
		setNextLocation(nextLoc);
		return false;
	};

	const closeModal = (confirm: boolean) => {
		setShowConfirmLeaveModal(false);
		if (confirm) {
			setMadeChanges(false); // Reset the changes flag
			history.push(nextLocation.pathname);
		}
	};

	const closeSaveModal = (confirm: boolean) => {
		if (confirm && !!confirmSaveCallback) {
			confirmSaveCallback();
			app.restartCore();
			app.restartEngine();
			setMadeChanges(false);
		}
		setConfirmSaveCallback(undefined);
		setShowConfirmSave(false);
	};

	function handleSetSave(pCallback: CallbackType) {
		setConfirmSaveCallback(() => pCallback);
		setShowConfirmSave(true);
	}

	return (
		<LayoutContent pTab={pTab} hideHeader={true}>
			<>
				<div className="full-size grid-col-18 vh-91">
					<div className="full-size flex-center align-center">
						<div className="flex-col align-center full-width-minus-pad full-height-minus-pad rounded">
							<SideMenu
								sections={Object.values(ConfigTypeDisplayNames).filter(v =>
									slipSheetPresent
										? true
										: v !== ConfigTypeDisplayNames[ConfigType.SLIPSHEET_MAP]
								)}
								selectedSection={ConfigTypeDisplayNames[selectedSection]}
								onSelectSection={(displayName: ConfigTypeValue) => {
									// Find the key for the display name
									const selectedKey = (
										Object.keys(ConfigTypeDisplayNames) as Array<
											keyof typeof ConfigTypeDisplayNames
										>
									).find(key => ConfigTypeDisplayNames[key] === displayName);
									if (selectedKey) {
										history.push(lBase + selectedKey);
									}
								}}
								unshaded={true}
							/>
						</div>
					</div>
					<div className="full-size flex-col border-left">
						<Switch>
							<Route exact path={lBase}>
								<Redirect to={lBase + ConfigType.General} />
							</Route>
							<Route path={lBase + ConfigType.General} component={AppSettings} />
							<Route
								path={lBase + ConfigType.AppConfig}
								render={() => (
									<AppConfigSettings
										handleMadeChanges={made => setMadeChanges(made)}
										slipSheetPresent={slipSheetPresent}
										confirmSave={handleSetSave}
										activeUser={selectedUserObject!}
									/>
								)}
							/>
							{/*<Route
								path={lBase + ConfigType.VRDFConfig}
								render={() => (
									<VRDFSettings
										handleMadeChanges={made => setMadeChanges(made)}
										confirmSave={handleSetSave}
									/>
								)}
								/>*/}
							<Route
								path={lBase + ConfigType.IO}
								render={() => (
									<IOSettings
										handleMadeChanges={made => setMadeChanges(made)}
										confirmSave={handleSetSave}
										activeUser={selectedUserObject!}
									/>
								)}
							/>
							<Route
								path={lBase + ConfigType.GRIPPER}
								render={() =>
									lMachineConfig ? (
										<Grippers
											slipSheetPresent={slipSheetPresent}
											handleMadeChanges={made => setMadeChanges(made)}
											confirmSave={handleSetSave}
										/>
									) : (
										<Loading />
									)
								}
							/>
							<Route
								path={lBase + ConfigType.SKU_SPECIFIC}
								render={() => (
									<SKUSpecific
										handleMadeChanges={made => setMadeChanges(made)}
										confirmSave={handleSetSave}
										activeUser={selectedUserObject!}
									/>
								)}
							/>
							<Route
								path={lBase + ConfigType.SLIPSHEET_MAP}
								render={() => (
									<SlipSheetSettings
										handleMadeChanges={made => setMadeChanges(made)}
										confirmSave={handleSetSave}
										activeUser={selectedUserObject!}
									/>
								)}
							/>
							<Route render={() => <Redirect to={lBase + ConfigType.General} />} />
						</Switch>
					</div>
				</div>
				<Prompt when={madeChanges} message={handleBlockedNavigation} />
				{showConfirmLeaveModal && (
					<ConfirmationModal
						t1={'You have unsaved changes.'}
						t2={'Are you sure you want to exit?'}
						bCancel={'Keep Editing'}
						bConfirm={'Discard Changes'}
						onConfirm={() => closeModal(true)}
						onCancel={() => closeModal(false)}
					/>
				)}
				{showConfirmSave && (
					<ConfirmationModal
						t1={'This will reconnect to the robot.'}
						t2={'Are you sure?'}
						bCancel={'Cancel'}
						bConfirm={'Save'}
						onConfirm={() => closeSaveModal(true)}
						onCancel={() => closeSaveModal(false)}
					/>
				)}
				{locked && (
					<Unlock
						close={() => {
							setLocked(false);
						}}
					></Unlock>
				)}
			</>
		</LayoutContent>
	);
}

function toHumanString(v: string) {
	return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
}

enum TCP_TYPE {
	SLIPSHEET = 0,
	PICK_1 = 1,
	PICK_2 = 2,
	PICK_3 = 3,
	PICK_4 = 4,
	PICK_5 = 5,
	PICK_6 = 6,
	PICK_7 = 7,
}

type RecursiveKeyFinder = {
	[key: string]: any;
};

function findFlatObjectWithSpecificValue(
	obj: RecursiveKeyFinder,
	targetKey: string,
	targetValue: boolean
): RecursiveKeyFinder {
	const resultObject: RecursiveKeyFinder = {};

	function traverse(
		currentObj: RecursiveKeyFinder,
		parentKey: string | null = null
	) {
		// Iterate through all properties of the current object
		for (const key of Object.keys(currentObj)) {
			const value = currentObj[key];
			// If the value is an object and not an array, continue the recursion
			if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
				// If the object itself contains the target key-value pair, store it under its key
				if (value.hasOwnProperty(targetKey) && value[targetKey] === targetValue) {
					resultObject[key] = value;
				}
				// Continue to traverse deeper into the object
				traverse(value, key);
			}
		}
	}

	// Start the traversal from the root object
	traverse(obj);
	return resultObject;
}

interface SKUSettings {
	gripper: string;
}

function SKUSpecific({
	handleMadeChanges,
	confirmSave,
	activeUser,
}: SettingBaseProps) {
	// creating new skus:
	///
	/// click new sku settings
	/// new entry in list...go into creating mode... must select sku from list...rest of sku settings disabled
	/// once selected then start edit + save etc.
	///
	const lKeyboardTrigger: KeyboardTrigger = useContext(KeyboardContext);

	const { all_configs } = useContext(ConfigurationsContext) as any;

	const app = useContext(ApplicationContext) as Application;

	const [config, setConfig] = useState<any>(void 0);
	const [original, setOriginal] = useState<any>(void 0);
	const [madeChanges, setMadeChanges] = useState<boolean>(false);
	const [selectedSkuID, setSelectedSkuID] = useState<string>('');
	const [selectedSkuSettings, setSelectedSkuSettings] = useState<any>(void 0);
	const [selectedBoxConfig, setSelectedBoxConfig] = useState<Box_t | undefined>(
		void 0
	);

	const [appConfigValues, setAppConfigValues] = useState<{ [key: string]: any }>(
		{}
	);
	const [modifiable, setModifiable] = useState<{ [key: string]: any }>({});

	useEffect(() => {
		handleMadeChanges(madeChanges);
	}, [madeChanges]);

	const [grippers, setGrippers] = useState<string[]>([]);

	const newSkuID = 'new setting';
	const defaultGripperName = 'Default';
	const defaultSkuSettings: SKUSettings = { gripper: defaultGripperName };

	const IS_WEB_DEPLOYMENT = isWebDeployment();

	async function fetchConfig() {
		try {
			let pConfig: any;
			if (IS_WEB_DEPLOYMENT) {
				const unparsedSkuConfig = localStorage.getItem('skuSpecific');
				pConfig = unparsedSkuConfig
					? JSON.parse(unparsedSkuConfig)
					: await app.api.SKUSpecific.get();
			} else {
				pConfig = await app.api.SKUSpecific.get();
			}
			setConfig(pConfig);
			setOriginal(JSON.parse(JSON.stringify(pConfig)));
			setMadeChanges(false);

			let selectedId = '';

			if (selectedSkuID == '') {
				selectedId = selectFirstSkuSetting(pConfig);
			}

			// Get grippers
			let pGripperConfig: any;
			if (IS_WEB_DEPLOYMENT) {
				const unparsedGripperConfig = localStorage.getItem('gripperConfig');
				pGripperConfig = unparsedGripperConfig
					? JSON.parse(unparsedGripperConfig)
					: await app.api.GripperConfig.get();
			} else {
				pGripperConfig = await app.api.GripperConfig.get();
			}
			setGrippers(Object.keys(pGripperConfig));

			// Get app config to find modifiable params
			const pAppConfig = fetchCoreAppConfig(app);
			if (!pAppConfig) return;

			setAppConfigValues(pAppConfig);

			const sku_ids = Object.keys(pConfig);
			const mod: { [key: string]: any } = {};
			const base = findFlatObjectWithSpecificValue(
				pAppConfig,
				'sku_specific',
				true
			);
			sku_ids.forEach(id => {
				mod[id] = JSON.parse(JSON.stringify(base));

				// Overwrite the matching keys with sku specific values.
				const acKeys = Object.keys(mod[id]);
				acKeys.forEach(app_config_key => {
					if (pConfig[id].hasOwnProperty(app_config_key)) {
						mod[id][app_config_key].v = pConfig[id][app_config_key];
					}
				});
			});
			setModifiable(mod);
		} catch (error) {
			console.error('Error in fetchConfig:', error);
		}
	}
	useEffect(function () {
		fetchConfig();
	}, []);

	useEffect(
		function () {
			if (!config) return;
			let id = selectedSkuID;
			if (!selectedSkuSettings) {
				const lSkus = Object.keys(config);
				if (lSkus.length === 0) return;
				id = lSkus[0];
				setSelectedSkuID(id);
			}
			// Always update the selected sku settings when we change config
			setSelectedSkuSettings(config[id]);
			setMadeChanges(JSON.stringify(config) != JSON.stringify(original));
		},
		[config]
	);

	function selectFirstSkuSetting(pOptionalConfig: any) {
		let c = config;
		if (pOptionalConfig !== undefined) {
			c = pOptionalConfig;
		}
		if (!c) return '';
		const lSkus = Object.keys(c);
		if (lSkus.length === 0) return '';
		setSelectedSkuID(lSkus[0]);
		setSelectedSkuSettings(c[lSkus[0]]);
		return lSkus[0];
	}

	// useEffect(() => {
	// 	if (!all_configs) return;
	// 	const lBoxes = all_configs.config.boxes;
	// }, []);

	const handleSelectSection = (section: string) => {
		setSelectedSkuID(section);
		setSelectedSkuSettings(config[section]);
	};

	async function saveChanges() {
		confirmSave(async () => {
			const save_config = { ...config };
			// Get modifiable values and write into config
			const sku_ids = Object.keys(modifiable);
			sku_ids.forEach(sku_id => {
				try {
					const keys = Object.keys(modifiable[sku_id]);
					for (let i = 0; i < keys.length; i++) {
						const obj = modifiable[sku_id][keys[i]];
						save_config[sku_id][keys[i]] = obj.v;
					}
				} catch {
					console.error('Failed to overwrite sku specific setting.');
				}
			});

			if (IS_WEB_DEPLOYMENT) {
				localStorage.setItem('skuSpecific', JSON.stringify(save_config));
			} else {
				await app.api.SKUSpecific.update(save_config);
			}
			fetchConfig();
		});
	}

	function cancelChanges() {
		setSelectedSkuID('');
		fetchConfig();
	}

	function addSkuSpecific() {
		const lNewSettings = { ...defaultSkuSettings };
		const lNewConfig = { ...config };
		lNewConfig[newSkuID] = lNewSettings;
		setConfig(lNewConfig);
		setSelectedSkuID(newSkuID);
		setSelectedSkuSettings(lNewSettings);

		const base = findFlatObjectWithSpecificValue(
			appConfigValues,
			'sku_specific',
			true
		);
		const mod = { ...modifiable };
		mod[newSkuID] = JSON.parse(JSON.stringify(base));
		setModifiable(mod);
	}

	function deleteSKUSettings() {
		if (selectedSkuID !== newSkuID) {
			app.api.SKUSpecific.delete(selectedSkuID).then().catch(console.error);
		}

		const lNewConfig = { ...config };
		delete lNewConfig[selectedSkuID];
		setConfig(lNewConfig);

		if (Object.keys(lNewConfig).length === 0) {
			fetchConfig();
		} else {
			selectFirstSkuSetting(undefined);
		}
	}

	function boxNameFromId(id: string) {
		if (id === newSkuID) return newSkuID;
		if (!all_configs) return '';

		for (let b = 0; b < all_configs.config.boxes.length; b++) {
			const box: Box_t = all_configs.config.boxes[b];
			if (box.id === id) return box.name;
		}

		return '';
	}

	const [skuSelectedForSetting, setSkuSelectedForSetting] =
		useState<boolean>(false);
	useEffect(() => {
		setSkuSelectedForSetting(!!selectedSkuID && selectedSkuID !== newSkuID);

		Object.keys(modifiable).forEach(sku_id => {
			if (sku_id === newSkuID) {
				modifiable[selectedSkuID] = modifiable[newSkuID];
				delete modifiable[newSkuID];
				return;
			}
		});
	}, [selectedSkuID]);

	return (
		<div className="full-size flex-col">
			<div className="flex-row full-width flex-between align-center vh-10 padding-top padding-bottom border-bottom">
				<span className="double-margin-left double-margin-top font-5 color-blue bold-font ">
					SKU Settings
				</span>
				<div className="flex-row full-height align-center">
					<Button
						className="vw-8 double-margin-right double-margin-top"
						pAction={cancelChanges}
						pNormalColorOverride={COLOR.DARK_BLUE}
					>
						<span className="bold-font">Cancel</span>
					</Button>
					<Button
						className="vw-8 double-margin-right double-margin-top"
						pAction={() => saveChanges()}
						pNormalColorOverride={madeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
						pDisabled={!madeChanges}
					>
						<span className="bold-font">Save</span>
					</Button>
				</div>
			</div>

			<div className="full-height full-width grid-col-20">
				<div className="full-size flex-col flex-start vh-91 align-center padding-top">
					{!!config && !!all_configs ? (
						<>
							<Button
								className="full-width-minus-pad margin-top margin-bottom"
								pAction={addSkuSpecific}
								pNormalColorOverride={COLOR.DARK_BLUE}
							>
								<span className="bold-font">Add SKU Settings</span>
							</Button>

							<SideMenu
								sections={Object.keys(config).map(id => {
									return boxNameFromId(id);
								})}
								sectionSubstituteValues={Object.keys(config)}
								selectedSection={selectedSkuID}
								onSelectSection={handleSelectSection}
								unshaded={true}
							/>
						</>
					) : (
						<Loading />
					)}
				</div>

				{!!selectedSkuSettings && all_configs && (
					<div className="flex-col align-center full-width full-height padding-top border-left">
						<div className="flex-col width-90" key={selectedSkuID}>
							<div className="double-padding"></div>
							<div className="flex-row full-width flex-between ">
								<div className="flex-col">
									<span className="font-5 margin-bottom bold-font color-blue">SKU</span>
									{(function renderSelect() {
										const lAvailBoxes = skuSelectedForSetting
											? all_configs.config.boxes
											: all_configs.config.boxes.filter(
													(b: Box_t) => !Object.keys(config).includes(b.id)
											  );
										return (
											<select
												className="StyledSelect"
												value={selectedSkuID}
												onChange={e => {
													const lNewConfig = { ...config };
													delete lNewConfig[newSkuID];
													lNewConfig[e.target.value] = { ...defaultSkuSettings };
													setConfig(lNewConfig);
													setSelectedSkuID(e.target.value);
													setSelectedSkuSettings(lNewConfig[e.target.value]);
													setMadeChanges(true);
												}}
												disabled={skuSelectedForSetting}
											>
												<option value={''}>{`${
													lAvailBoxes.length > 0
														? 'Select a box'
														: 'Create new box or modify existing setting'
												}`}</option>
												{lAvailBoxes.map((b: Box_t) => (
													<option key={b.id} value={b.id}>
														{b.name}
													</option>
												))}
											</select>
										);
									})()}
								</div>

								<Button
									className="vw-12 margin-left "
									pAction={deleteSKUSettings}
									pNormalColorOverride={COLOR.DARK_BLUE}
								>
									<span className="bold-font">Delete SKU Settings</span>
								</Button>
							</div>
						</div>
						<div className="double-padding"></div>

						{skuSelectedForSetting && (
							<div className="flex-col width-90">
								<span className="font-5 margin-bottom bold-font color-blue">
									Gripper
								</span>
								<select
									className="StyledSelect"
									value={
										selectedSkuSettings?.gripper
											? selectedSkuSettings.gripper
											: defaultGripperName
									}
									onChange={e => {
										const lNewConfig = { ...config };
										lNewConfig[selectedSkuID]['gripper'] = e.target.value;
										setConfig(lNewConfig);
									}}
								>
									<option value={defaultGripperName}>{defaultGripperName}</option>
									{grippers.map((g, gIdx) => (
										<option key={gIdx} value={g}>
											{g}
										</option>
									))}
								</select>
							</div>
						)}
						{skuSelectedForSetting && !!modifiable && !!modifiable[selectedSkuID] && (
							<div className="full-width full-height">
								<SettingsEditor
									settings={modifiable[selectedSkuID]}
									onChange={(key, value) => {
										const lNewMod = { ...modifiable };
										lNewMod[selectedSkuID][key].v = value;
										setModifiable(lNewMod);
										setMadeChanges(true);
									}}
									activeUser={activeUser}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export function SlipSheetSettings({
	handleMadeChanges,
	confirmSave,
}: SettingBaseProps) {
	const app = useContext(ApplicationContext) as Application;

	const { all_configs } = useContext(ConfigurationsContext) as any;
	const { preferences } = useContext(PreferencesContext) as any;

	// Temp duplication of keys for now:
	const [lPickPoints, lSetPickPoints] = useState<string[][]>([
		['Slip Sheet Pick Left 1', 'Slip Sheet Pick Left 2'],
		['Slip Sheet Pick Right 1', 'Slip Sheet Pick Right 2'],
	]);
	const [lActivePickPointIdx, lSetActivePickPointIdx] = useState<{
		side: number;
		idx: number;
	}>({
		side: 0,
		idx: 0,
	});
	const [lAllSlipSheets, lSetAllSlipSheets] = useState<SlipSheet_t[]>([]);
	const [lAvailableSlipSheets, lSetAvailableSlipSheets] = useState<
		SlipSheet_t[][]
	>([]);
	// const [lAssociatedSSIds, lSetAssociatedSSIds] = useState<string[]>([]);
	const [lMadeChanges, lSetMadeChanges] = useState<boolean>(false);

	useEffect(() => {
		handleMadeChanges(lMadeChanges);
	}, [lMadeChanges]);

	const [saved_calibration, set_saved_calibration] = useState<any>(null);
	const [editing_calibration, set_editing_calibration] = useState<any>(null);

	useEffect(() => {
		if (all_configs?.config?.slipsheets) {
			const all = all_configs.config.slipsheets as SlipSheet_t[];
			lSetAllSlipSheets(all);
			if (!!editing_calibration) {
				const lAvail: SlipSheet_t[][] = [[], []];
				for (let b = 0; b < lPickPoints.length; b++) {
					const sidePoints = lPickPoints[b];
					let allTakenIds: string[] = [];
					for (const pickPoint of sidePoints) {
						allTakenIds = allTakenIds.concat(
							editing_calibration[pickPoint].ss_ids || []
						);
					}
					lAvail[b] = all.filter(ss => !allTakenIds.includes(ss.id));
				}
				lSetAvailableSlipSheets(lAvail);
			}
		}
		lSetMadeChanges(
			JSON.stringify(editing_calibration) !== JSON.stringify(saved_calibration)
		);
	}, [all_configs, editing_calibration]);

	useEffect(() => {
		if (!app?.api) return;
		app.api.Generic.get('CALIBRATION').then(function (data) {
			const c = to_calibration(data) as Calibration_t;
			set_saved_calibration(c);
			set_editing_calibration(JSON.parse(JSON.stringify(c)));
		});
	}, []);

	const [lSSForSelectedPick, lSetSSForSelectedPick] = useState<SlipSheet_t[]>(
		[]
	);
	useEffect(() => {
		if (!editing_calibration) return;
		const calib = editing_calibration[
			lPickPoints[lActivePickPointIdx.side][lActivePickPointIdx.idx]
		] as SlipSheetPickCalibration_t;
		if (calib.ss_ids) {
			const searchIds = calib.ss_ids;
			const temp = [];
			for (const ss of lAllSlipSheets) {
				if (searchIds.includes(ss.id)) {
					temp.push(ss);
				}
			}
			lSetSSForSelectedPick(temp);
		}
	}, [lAllSlipSheets, editing_calibration, lActivePickPointIdx]);

	const [lSaveButtonText, lSetSaveButtonText] = useState<string>('Save');

	const save_changes = () => {
		confirmSave(() => {
			set_saved_calibration(JSON.parse(JSON.stringify(editing_calibration)));
			app.api.Generic.set(['CALIBRATION'], editing_calibration)
				.then(function (result) {
					lSetSaveButtonText('Saved!');
					setTimeout(() => {
						lSetSaveButtonText('Save');
						lSetMadeChanges(false);
					}, 1000);
				})
				.catch(console.error);
		});
	};

	return (
		<div className="flex-col full-size background-white">
			<div className="flex-row height-9 full-width double-padding-left double-padding-right">
				<div className="full-width full-height flex-row align-center flex-between double-padding-right">
					<span className="font-5 bold-font">Define slip sheet pick location</span>
					<Button
						pAction={save_changes}
						pNormalColorOverride={lMadeChanges ? COLOR.LIGHT_BLUE : COLOR.DARK_GREY}
						pClickColorOverride={COLOR.LIGHT_BLUE}
						className="double-margin-right width-10"
						pDisabled={!lMadeChanges}
					>
						<span className="bold-font vw-8">{lSaveButtonText}</span>
					</Button>
				</div>
			</div>

			<div className="flex-row height-91 flex-between double-padding-left double-padding-right double-padding-bottom">
				<div className="width-30">
					<div className="full-height full-width border flex-col rounded">
						<div className="background-silver full-width vh-6 rounded-top flex-row align-center ">
							<span className="color-blue font-5 double-padding">Magazines</span>
						</div>
						<div className="flex-col">
							{lPickPoints.map((pSidePoints, pSideIdx) => {
								return pSidePoints.map((pp, pIdx) => {
									return (
										<div
											key={pIdx}
											className={
												'full-width margin-bottom double-padding-top double-padding-bottom ' +
												`${
													pSideIdx === lActivePickPointIdx.side &&
													pIdx === lActivePickPointIdx.idx
														? 'background-medium-blue'
														: ''
												}`
											}
											onClick={() => {
												lSetActivePickPointIdx({ side: pSideIdx, idx: pIdx });
											}}
										>
											<span
												className={
													'font-5 margin-left ' +
													`${
														pSideIdx === lActivePickPointIdx.side &&
														pIdx === lActivePickPointIdx.idx
															? 'color-white'
															: 'color-blue'
													}`
												}
											>
												{pp}
											</span>
										</div>
									);
								});
							})}
						</div>
					</div>
				</div>
				<div className="full-height width-35 flex-col">
					<div className="full-width vh-6 flex-row align-center">
						<span className="font-5 color-blue">Selected</span>
					</div>
					<div className="flex-col full-width">
						{lSSForSelectedPick.map((pSS, pIdx) => {
							return (
								<div
									key={pIdx}
									className={
										'flex-row background-silver align-center flex-between full-width rounded margin-bottom padding-top padding-bottom ' +
										`${false ? 'blue-inner-border' : ''}`
									}
									onClick={() => {}}
								>
									<span className={'font-5 margin-left color-blue '}>
										{pSS.name +
											', height: ' +
											render_length(pSS.dimensions.height, preferences.units_system) +
											render_length_unit(preferences.units_system)}
									</span>
									<Button
										pAction={() => {
											const lCp = { ...editing_calibration };
											const ids = lCp[
												lPickPoints[lActivePickPointIdx.side][lActivePickPointIdx.idx]
											].ss_ids as string[];
											const idx = ids.findIndex(id => id === pSS.id);
											ids.splice(idx, 1);
											set_editing_calibration(lCp);
										}}
										className="double-margin-right"
										pNormalColorOverride={COLOR.TRANSPARENT}
									>
										<span className="font-5 double-padding icon-minus bold-font color-dark-grey" />
									</Button>
								</div>
							);
						})}
					</div>
				</div>
				<div className="full-height width-30">
					<div className="full-height full-width border rounded flex-col flex-between ">
						<div className="background-medium-blue full-width vh-6 rounded-top flex-row align-center">
							<span className="color-white font-5 double-padding">
								Unmapped Slip Sheets
							</span>
						</div>
						<Scroll>
							<div>
								{lAvailableSlipSheets.length > 0 && !!preferences?.units_system ? (
									lAvailableSlipSheets[lActivePickPointIdx.side].map((pSS, pIdx) => {
										return (
											<div
												key={pIdx}
												className={
													'flex-row align-center flex-between full-width border rounded margin-bottom double-padding-top double-padding-bottom ' +
													`${false ? 'blue-inner-border' : ''}`
												}
												onClick={() => {}}
											>
												<span
													className={
														'font-5 margin-left color-blue ' + `${false ? 'bold-font ' : ''}`
													}
												>
													{pSS.name +
														', height: ' +
														render_length(pSS.dimensions.height, preferences.units_system) +
														render_length_unit(preferences.units_system)}
												</span>
												<Button
													pAction={() => {
														const lCp = { ...editing_calibration };
														lCp[
															lPickPoints[lActivePickPointIdx.side][lActivePickPointIdx.idx]
														].ss_ids.push(pSS.id);
														set_editing_calibration(lCp);
													}}
													className="double-margin-right"
													pDisabled={lSSForSelectedPick.length > 0}
													pNormalColorOverride={
														lSSForSelectedPick.length > 0
															? COLOR.DARK_GREY
															: COLOR.MEDIUM_BLUE
													}
												>
													<span className="double-padding"> Add </span>
												</Button>
											</div>
										);
									})
								) : (
									<div className="full-size flex-row flex-center align-center">
										<span className="font-5">No more slip sheets.</span>
									</div>
								)}
							</div>
						</Scroll>
					</div>
				</div>
			</div>
		</div>
	);
}
